import { type ReactElement, useEffect, useState } from 'react';
import { type GovernedSettingBucketView } from '@vello/api-client';
import { MoneyInput, Notice, Stack, Measure } from '@vello/web-theme';
import { countryPackCurrencyAt, moneySettingCurrency, moneySettingOf, moneySettingValue } from '../../src/index';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';

export interface MoneySettingEditorProps {
  /** The draft value (`{ amountMinor, currency }`, or anything the operator had before). */
  readonly value: unknown;
  /** The bucket being edited — its version in force and its country (the currency source). */
  readonly bucket: GovernedSettingBucketView | undefined;
  /** The key's registered default, a last currency source. */
  readonly defaultValue: unknown;
  /** The server's clock of the read (a country pack is picked by the window holding it). */
  readonly now: string;
  readonly onValue: (value: unknown) => void;
  /** Rendered when no currency can be told — the raw editor, so the key stays editable. */
  readonly fallback: ReactElement;
}

/**
 * The money editor of a governed amount (W8, D438; finding C-18) — `finance.refundFourEyesThreshold` is
 * `{ amountMinor, currency }`, and until D438 it was typed as raw JSON. Here it is the design-system `MoneyInput`: the
 * operator types the amount in the currency's own convention and the console writes integer minor units (a safe-integer
 * number, which the kernel's validator requires) with the currency.
 *
 * The currency is never typed: it is the one the draft or the version in force already carries, else the bucket's
 * country pack (the APPROVED pack in force, `GET /v1/config/country-packs/:cc`). When neither can tell, the screen says so
 * and keeps the raw editor rather than guessing a currency.
 */
export function MoneySettingEditor({ value, bucket, defaultValue, now, onValue, fallback }: MoneySettingEditorProps): ReactElement {
  const { runtime } = useAdmin();
  const { t, lang } = useT();
  const { v } = useVocab();
  const known = moneySettingCurrency([value, bucket?.current?.value, bucket?.base.value, defaultValue]);
  const country = bucket?.scope === 'country' ? bucket.scopeRef : undefined;
  const [pack, setPack] = useState<{ country: string; currency: string | undefined } | undefined>(undefined);

  useEffect(() => {
    if (known !== undefined || country === undefined || country === '') return undefined;
    let alive = true;
    void (async () => {
      const res = await runtime.config.listCountryPacks(country);
      if (!alive) return;
      setPack({ country, currency: res.ok ? countryPackCurrencyAt(res.value.packs, now) : undefined });
    })();
    return () => {
      alive = false;
    };
  }, [runtime, known, country, now]);

  const packCurrency = pack !== undefined && pack.country === country ? pack.currency : undefined;
  const currency = known ?? packCurrency;
  if (currency === undefined) {
    const looking = country !== undefined && country !== '' && (pack === undefined || pack.country !== country);
    return (
      <Stack gap={3}>
        <Notice tone={looking ? 'neutral' : 'warning'}>{t(looking ? 'govset.editor.money.loading' : 'govset.editor.money.noCurrency')}</Notice>
        {!looking && fallback}
      </Stack>
    );
  }

  const current = moneySettingOf(value);
  return (
    <Measure size="sm">
      <MoneyInput
        id="govset-money"
        label={t('govset.editor.money')}
        currency={currency}
        locale={lang}
        valueMinor={current !== undefined && current.currency === currency ? current.amountMinor : undefined}
        onChangeMinor={(minor) => onValue(moneySettingValue(minor, currency))}
        issueText={(issue) => v('format.money_input_issue', issue)}
        hint={t('govset.editor.money.hint', { currency })}
        maxMinor={BigInt(Number.MAX_SAFE_INTEGER)}
        required
      />
    </Measure>
  );
}
